import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import "@/i18n";
import App from "@/App";
import { toast } from "sonner";

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

// ---------------------------------------------------------------------------
// PWA Service Worker registration + AUTO-UPDATE strategy
// ---------------------------------------------------------------------------
// Strategia iter 30k per garantire che l'utente veda SEMPRE la versione più
// recente dell'app senza dover svuotare manualmente la cache:
//   1. Controllo `reg.update()` ogni 30 secondi (in background, non bloccante).
//   2. Stesso controllo quando la tab torna in focus (visibilitychange).
//   3. Quando il browser scarica un nuovo SW → mostra una toast non-bloccante
//      "Nuova versione disponibile" con bottone "Aggiorna".
//   4. Click sul bottone → invia `SKIP_WAITING` al nuovo SW → controllerchange
//      → ricarica la pagina (i nuovi bundle JS vengono fetchati freschi).
// L'utente NON viene mai forzato a ricaricare durante una digitazione attiva:
// il banner resta visibile FINCHÉ l'utente non clicca "Aggiorna" o non lo
// chiude esplicitamente (durata infinita — iter 31u-1 su richiesta utente).
// ---------------------------------------------------------------------------
if ("serviceWorker" in navigator) {
  let _isReloading = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (_isReloading) return;
    _isReloading = true;
    window.location.reload();
  });

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js")
      .then((reg) => {
        // Iter 31u-2: evita che la toast venga riemessa più volte (lampeggio).
        // Il polling di update e gli eventi multipli statechange potrebbero
        // chiamare promptUpdate ripetutamente per lo stesso worker. Tracciamo
        // i worker già notificati con un WeakSet — il toast sonner con stesso
        // id viene "replace-rendered" ogni volta, creando un flash visivo.
        // Iter 45: aggiornamento AUTOMATICO per tutti gli utenti — appena il
        // nuovo SW è pronto lo attiviamo subito (niente click "Aggiorna");
        // controllerchange fa il reload e al ritorno mostriamo la conferma
        // "stai usando l'ultima versione".
        const _notifiedWorkers = new WeakSet();

        const promptUpdate = (waitingWorker) => {
          if (!waitingWorker) return;
          if (_notifiedWorkers.has(waitingWorker)) return;
          _notifiedWorkers.add(waitingWorker);
          try { sessionStorage.setItem("app_just_updated", "1"); } catch { /* ignore */ }
          toast.info("Nuova versione trovata: aggiornamento automatico in corso…", {
            id: "sw-update-toast",
            duration: 4000,
          });
          waitingWorker.postMessage({ type: "SKIP_WAITING" });
        };

        // Se al primo load c'è già un SW in 'waiting' (perché era stato
        // installato in un caricamento precedente), avvisa subito.
        if (reg.waiting) promptUpdate(reg.waiting);

        // Quando il browser trova un nuovo SW
        reg.addEventListener("updatefound", () => {
          const newWorker = reg.installing;
          if (!newWorker) return;
          newWorker.addEventListener("statechange", () => {
            if (
              newWorker.state === "installed" &&
              navigator.serviceWorker.controller
            ) {
              // C'era già un SW attivo → questo è un AGGIORNAMENTO
              promptUpdate(newWorker);
            }
            // Se state==installed e nessun controller → primo install (no toast)
          });
        });

        // Background polling per nuovi update (ogni 30 secondi)
        const checkForUpdates = () => {
          reg.update().catch(() => {
            /* offline o errore: silenzioso, riproveremo */
          });
        };
        setInterval(checkForUpdates, 30 * 1000);

        // Check anche quando la tab torna visibile (utente riprende l'app)
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "visible") {
            checkForUpdates();
          }
        });

        // E al focus della finestra (PWA installata: minimizzata→ripresa)
        window.addEventListener("focus", checkForUpdates);
      })
      .catch((err) => console.warn("SW registration failed:", err));
  });
}
